import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  addRouteDestinations,
  deleteRoute,
  testConnectionAction,
  updateConnection,
  updateRouteConfiguration,
} from 'api/routes/routesApi';
import {
  ConnectionConfiguration,
  Destination,
  Provider,
} from 'components/common/ObjectTypes';
import { trackRouteDisabled, trackRouteEnabled } from 'analytics/analytics';
import {
  FieldMappingsConfig,
  buildConfigWithoutRule,
  getRuleForDestination,
} from '../transformationRules/fieldMappingTypes';
import { routeTabPath } from '../routeSubpages';
import type { RouteRecord } from './useRouteRecord';

// What the flow map does to the Route's accounts: test an action, switch the
// Route on or off, save the webhook settings, and remove a destination.
export const useFlowMapActions = ({
  record,
  reverseRoutesBySite,
  refreshReverseRoutes,
  routeConfigData,
  routeConfigurationName,
}: {
  record: RouteRecord;
  reverseRoutesBySite: Record<string, string[]>;
  refreshReverseRoutes: () => void;
  routeConfigData: FieldMappingsConfig;
  routeConfigurationName: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    getToken,
    routeId,
    route,
    routeProvider,
    setRouteProvider,
    destinations,
    setDestinations,
    defaultRouteId,
    changesMade,
    setChangesMade,
  } = record;

  const [testActionLoading, setTestActionLoading] = useState(false);
  const [testActionData, setTestActionData] = useState<any>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  async function handleToggleProviderEnabled(e: any) {
    const updatedRoute = {
      enabled: e,
    };
    const token = getToken();
    if (token) {
      try {
        // A swallowed 401 comes back as undefined — not a save.
        const saved = await updateConnection(
          token,
          routeProvider.id,
          updatedRoute,
        );
        if (!saved) return;
        toast.success(`${t('Route updated')}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        setRouteProvider((prevProvider: Provider) => ({
          ...prevProvider,
          enabled: e,
        }));
        if (e) {
          trackRouteEnabled(
            routeProvider.id,
            routeProvider.type?.name,
            route.owner?.name,
          );
        } else {
          trackRouteDisabled(
            routeProvider.id,
            routeProvider.type?.name,
            route.owner?.name,
          );
        }
        setChangesMade(false);
      } catch (error) {
        console.log(error);
      }
    }
  }

  // `target` is the account the action belongs to — the flow map tests destination
  // accounts too, and an action must run against its own integration, not the
  // provider's. It defaults to the provider for screens that only show one account.
  const handleTestAction = async (
    action_id: string,
    action_type: string,
    target?: { id: string; configurations: any[] },
    formData?: Record<string, unknown>,
  ) => {
    const token = getToken();
    if (token) {
      setTestActionLoading(true);
      const account = target ?? {
        id: route.provider.id,
        configurations: routeProvider.configurations,
      };
      const stored = (account.configurations || []).find(
        (a: ConnectionConfiguration) => a.action.id === action_id,
      );
      const form_data = formData ?? stored?.data ?? {};

      try {
        let result = await testConnectionAction(
          token,
          account.id,
          action_type,
          { config_overrides: Object(form_data) },
        );
        if (result) {
          let { data: resp_data } = result;
          setTestActionData(resp_data);
        }
      } catch (err: any) {
        setTestActionData({
          error:
            err?.response?.data?.detail ||
            err?.message ||
            'Server error. Please try again.',
        });
      } finally {
        setTestActionLoading(false);
      }
    }
  };

  async function handleUpdateWebhookConfiguration(data: Record<string, any>) {
    // `webhook` comes back as the record on some responses and as a bare id
    // on others.
    const webhookRef = routeProvider.webhook_configuration?.webhook;
    const webhookId =
      (typeof webhookRef === 'string' ? webhookRef : webhookRef?.id) ||
      routeProvider.type?.webhook?.id;
    // Whatever stops the save has to say so, or Save looks dead.
    if (!webhookId) {
      toast.error(`${t('Failed to save webhook configuration')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    // No message for an expired session: the axios interceptor raises the
    // login modal, and calling it a webhook failure sends the user hunting
    // through their configuration.
    const token = getToken();
    if (token) {
      try {
        // A swallowed 401 comes back as undefined — not a save, so the
        // draft stays put. Same check as useConfigEditor's.
        const saved = await updateConnection(token, routeProvider.id, {
          webhook_configuration: {
            webhook: webhookId,
            data,
          },
        });
        if (!saved) return;
        toast.success(`${t('Connection updated')}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        setChangesMade(false);
      } catch (error) {
        console.log(error);
        toast.error(`${t('Failed to save webhook configuration')}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    }
  }

  // Cancelling the webhook settings leaves the page, asking first when there
  // are unsaved changes.
  function handleCancelWebhook() {
    if (changesMade) {
      setShowConfirmationModal(true);
    } else {
      navigate('/');
      setChangesMade(false);
    }
  }

  const handleConfirmNavigation = () => {
    setShowConfirmationModal(false);
    setChangesMade(false);
    navigate('/');
  };

  const handleCancelNavigation = () => {
    setShowConfirmationModal(false);
  };

  const handleRemoveDestination = async (dest: Destination) => {
    // The removed destination is NOT put back into the available list here.
    // Every surface that shows that list refetches it on open, so the server
    // is the source of truth — the optimistic push only ever produced stale
    // rows (and, before the dedupe, duplicate React keys) that flashed in the
    // drawer ahead of the real list and stole clicks meant for other rows.
    // An expired session sends nothing, so it changes nothing on screen
    // either; the login modal takes it from here.
    const token = getToken();
    if (!token) return;
    const remaining = destinations.filter(
      (currentDest: Destination) => currentDest.id !== dest.id,
    );
    setDestinations(remaining);
    const destination_ids = {
      destinations: remaining.map((a) => a.id),
    };
    setChangesMade(true);
    // The destinations list lives on the Route's underlying route, so without one
    // there is nothing to PATCH — say so instead of failing silently.
    // Local name: this is the route RECORD id the PATCHes target. It is
    // never the page's :route_id URL param, the provider integration id.
    const recordId = defaultRouteId;
    if (!recordId) {
      console.log(
        'cannot remove destination: route has no default route',
        route.id,
      );
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      setDestinations(destinations);
      setChangesMade(false);
      return;
    }
    // A destination that sends data back has its own reverse route, and the
    // arrows for it are drawn from route.destinations — detaching without
    // deleting it left that route running with nothing in this Route able to
    // stop it. Delete it first: if the PATCH below fails the user still sees
    // the destination, but nothing is silently pumping data.
    const reverseIds = reverseRoutesBySite[dest.id] || [];
    try {
      for (const reverseId of reverseIds) await deleteRoute(token, reverseId);
    } catch (err: any) {
      console.log(
        'failed to delete reverse route while removing destination',
        err,
      );
      if (err?.response?.status !== 401) {
        toast.error(
          `${t('Something went wrong. Please try again or contact support.')}`,
          { position: toast.POSITION.TOP_RIGHT },
        );
      }
      setDestinations(destinations);
      setChangesMade(false);
      return;
    }
    try {
      await addRouteDestinations(token, recordId, destination_ids);
      // Its field mapping is per flow, so it goes with the flow — otherwise
      // re-attaching the same destination silently revives the old rule.
      if (
        getRuleForDestination(routeConfigData, routeProvider?.id ?? '', dest.id)
      ) {
        try {
          await updateRouteConfiguration(token, recordId, {
            name: routeConfigurationName || route.provider?.name || '',
            data: buildConfigWithoutRule(
              routeConfigData,
              routeProvider?.id ?? '',
              dest.id,
            ),
          });
        } catch (ruleErr) {
          // The destination is detached either way; a stale mapping is
          // not worth failing the removal over.
          console.log("failed to drop the removed destination's rule", ruleErr);
        }
      }
      toast.success(`${t('Route updated')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      if (reverseIds.length > 0) refreshReverseRoutes();
      // The PATCH targets the route record; the URL keeps the provider
      // integration id the page is already addressed by. Routed through
      // routeTabPath so this cannot mint /routes/:id/configurations,
      // which the router answers with a redirect — the page would tear
      // down and rebuild, losing the selection the removal just set.
      navigate(routeTabPath(routeId, 'configurations'));
      setChangesMade(false);
    } catch (error) {
      console.log(error);
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      // Nothing was detached: put the destination back, like the
      // failures above, so the next attach or remove sends the real list.
      setDestinations(destinations);
      setChangesMade(false);
    }
  };

  return {
    testActionLoading,
    testActionData,
    showConfirmationModal,
    handleToggleProviderEnabled,
    handleTestAction,
    handleUpdateWebhookConfiguration,
    handleCancelWebhook,
    handleConfirmNavigation,
    handleCancelNavigation,
    handleRemoveDestination,
  };
};
